import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Divider,
  Autocomplete,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { apiService, Item, Customer } from '../../services/apiService';

const EditOrder: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [items, setItems] = useState<Item[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [formData, setFormData] = useState({
    teckit_no: '',
    item_name: '',
    campaign_name: '',
    voucher_name: '',
    voucher_number: '',
    period: '',
    unit_price: '',
    quantity: '',
    created_by: '',
    creation_date: '',
    coordinated_by: '',
    coordinated_date: '',
    feedback_by: '',
    feedback_date: '',
    effective_start_date: '',
    effective_end_date: '',
    followed_by: '',
    follow_status: '',
    collection_date: '',
    collection_time: '',
    notes: '',
    status: 'pending',
    customer_id: ''
  });

  useEffect(() => {
    if (id) {
      fetchOrderData();
    }
    fetchDropdownData();
  }, [id]);

  const fetchDropdownData = async () => {
    try {
      const [itemsResponse, customersResponse] = await Promise.all([
        apiService.getItems(),
        apiService.getCustomers()
      ]);

      setItems(itemsResponse.data || []);
      setCustomers(customersResponse.data || []);
    } catch (error: any) {
      console.error('Failed to fetch dropdown data:', error);
      toast.error('Failed to load dropdown data');
    }
  };

  const fetchOrderData = async () => {
    try {
      setFetching(true);
      const response = await apiService.getOrder(parseInt(id!));
      const order = response.data;
      
      setFormData({
        teckit_no: order.teckit_no || '',
        item_name: order.item_name || '',
        campaign_name: order.campaign_name || '',
        voucher_name: order.voucher_name || '',
        voucher_number: order.voucher_number || '',
        period: order.period || '',
        unit_price: order.unit_price?.toString() || '',
        quantity: order.quantity?.toString() || '',
        created_by: order.created_by || '',
        creation_date: order.creation_date || '',
        coordinated_by: order.coordinated_by || '',
        coordinated_date: order.coordinated_date || '',
        feedback_by: order.feedback_by || '',
        feedback_date: order.feedback_date || '',
        effective_start_date: order.effective_start_date || '',
        effective_end_date: order.effective_end_date || '',
        followed_by: order.followed_by || '',
        follow_status: order.follow_status || '',
        collection_date: order.collection_date || '',
        collection_time: order.collection_time ? order.collection_time.substring(0, 5) : '',
        notes: order.notes || '',
        status: order.status || 'pending',
        customer_id: order.customer_id?.toString() || ''
      });
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch order data');
      navigate('/orders');
    } finally {
      setFetching(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleItemChange = (item: Item | null) => {
    if (item) {
      setFormData(prev => ({
        ...prev,
        item_name: item.name,
        unit_price: item.unit_price.toString()
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        item_name: '',
        unit_price: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Convert string values to appropriate types
      const submitData = {
        ...formData,
        unit_price: formData.unit_price ? parseFloat(formData.unit_price) : undefined,
        quantity: formData.quantity ? parseInt(formData.quantity) : undefined,
        status: formData.status as 'pending' | 'in_progress' | 'completed' | 'cancelled',
        customer_id: formData.customer_id ? parseInt(formData.customer_id) : undefined,
        collection_time: formData.collection_time ? `${formData.collection_time}:00` : undefined
      };

      await apiService.updateOrder(parseInt(id!), submitData);
      toast.success('Order updated successfully!');
      navigate('/orders');
    } catch (error: any) {
      toast.error(error.message || 'Failed to update order');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/orders');
  };

  if (fetching) {
    return (
      <Box sx={{ backgroundColor: '#f0f9ff', minHeight: '100vh', p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f0f9ff', minHeight: '100vh', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleCancel} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1f2937', fontSize: '2rem' }}>
          Edit Order
        </Typography>
      </Box>

      <Card sx={{ 
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
        borderRadius: '16px',
        backgroundColor: 'white',
        border: 'none'
      }}>
        <CardContent sx={{ p: 4 }}>
          <form onSubmit={handleSubmit}>
            {/* Item Details Section */}
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1f2937', mb: 3 }}>
              Item Details
            </Typography>
            
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Ticket No."
                  value={formData.teckit_no}
                  InputProps={{
                    readOnly: true,
                  }}
                  variant="outlined"
                  sx={{
                    '& .MuiInputBase-input': {
                      backgroundColor: '#f5f5f5',
                    }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  options={customers}
                  getOptionLabel={(customer) => `${customer.company_name} - ${customer.contact_person}`}
                  value={customers.find(c => c.id.toString() === formData.customer_id) || null}
                  onChange={(event, newValue) => {
                    handleInputChange('customer_id', newValue?.id?.toString() || '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Customer"
                      placeholder="Select Customer"
                      variant="outlined"
                      required
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  options={items}
                  getOptionLabel={(item) => `${item.name} - ${item.item_code}`}
                  value={items.find(i => i.name === formData.item_name) || null}
                  onChange={(event, newValue) => {
                    handleItemChange(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Item"
                      placeholder="Select Item"
                      variant="outlined"
                      required
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Unit Price"
                  value={formData.unit_price}
                  InputProps={{
                    readOnly: true,
                  }}
                  variant="outlined"
                  sx={{
                    '& .MuiInputBase-input': {
                      backgroundColor: '#f5f5f5',
                    }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Campaign Name"
                  value={formData.campaign_name}
                  onChange={(e) => handleInputChange('campaign_name', e.target.value)}
                  placeholder="Enter"
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Voucher Name"
                  value={formData.voucher_name}
                  onChange={(e) => handleInputChange('voucher_name', e.target.value)}
                  placeholder="Enter"
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Voucher Number"
                  value={formData.voucher_number}
                  onChange={(e) => handleInputChange('voucher_number', e.target.value)}
                  placeholder="Enter"
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Period"
                  value={formData.period}
                  onChange={(e) => handleInputChange('period', e.target.value)}
                  placeholder="Enter"
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Unit Price"
                  type="number"
                  value={formData.unit_price}
                  onChange={(e) => handleInputChange('unit_price', e.target.value)}
                  placeholder="Enter"
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Quantity"
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => handleInputChange('quantity', e.target.value)}
                  placeholder="Enter"
                  variant="outlined"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Creator Details Section */}
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1f2937', mb: 3 }}>
              Creator Details
            </Typography>
            
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Created By"
                  value={formData.created_by}
                  onChange={(e) => handleInputChange('created_by', e.target.value)}
                  placeholder="Enter"
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Creation Date"
                  type="date"
                  value={formData.creation_date}
                  onChange={(e) => handleInputChange('creation_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Coordinated By"
                  value={formData.coordinated_by}
                  onChange={(e) => handleInputChange('coordinated_by', e.target.value)}
                  placeholder="Enter"
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Coordinated Date"
                  type="date"
                  value={formData.coordinated_date}
                  onChange={(e) => handleInputChange('coordinated_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Feedback By"
                  value={formData.feedback_by}
                  onChange={(e) => handleInputChange('feedback_by', e.target.value)}
                  placeholder="Enter"
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Feedback Date"
                  type="date"
                  value={formData.feedback_date}
                  onChange={(e) => handleInputChange('feedback_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Follow-Up Section */}
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1f2937', mb: 3 }}>
              Follow-Up
            </Typography>
            
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Effective Start Date"
                  type="date"
                  value={formData.effective_start_date}
                  onChange={(e) => handleInputChange('effective_start_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Effective End Date"
                  type="date"
                  value={formData.effective_end_date}
                  onChange={(e) => handleInputChange('effective_end_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Followed By"
                  value={formData.followed_by}
                  onChange={(e) => handleInputChange('followed_by', e.target.value)}
                  placeholder="Enter"
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Follow Status"
                  value={formData.follow_status}
                  onChange={(e) => handleInputChange('follow_status', e.target.value)}
                  placeholder="Enter"
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Collection Date"
                  type="date"
                  value={formData.collection_date}
                  onChange={(e) => handleInputChange('collection_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Collection Time"
                  type="time"
                  value={formData.collection_time}
                  onChange={(e) => handleInputChange('collection_time', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Enter notes..."
                  variant="outlined"
                />
              </Grid>
            </Grid>

            {/* Form Actions */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              pt: 3,
              borderTop: '1px solid #e2e8f0'
            }}>
              <Button
                onClick={handleCancel}
                variant="outlined"
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 500,
                  borderColor: '#e2e8f0',
                  color: '#64748b',
                  '&:hover': {
                    borderColor: '#cbd5e1',
                    backgroundColor: '#f8fafc',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                startIcon={<SaveIcon />}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: '12px',
                  backgroundColor: '#4A90E2',
                  color: 'white',
                  fontWeight: 500,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#3b82f6',
                  },
                }}
              >
                {loading ? 'Updating...' : 'Update Order'}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EditOrder;
